import { routeUrl } from "@/app/routes";
import type { CopilotActionView } from "@/features/copilot/schemas/copilot.schema";

interface RecordLink {
  href: string;
  label: string;
}

type LinkBuilder = (slug: string, action: CopilotActionView) => RecordLink | null;

function recordId(action: CopilotActionView): string | null {
  return action.result?.recordId ?? null;
}

function stringArg(action: CopilotActionView, name: string): string | null {
  const value = action.args[name];
  return typeof value === "string" && value.length > 0 ? value : null;
}

function byRecord(label: string, detail: (slug: string, id: string) => string): LinkBuilder {
  return (slug, action) => {
    const id = recordId(action);
    return id ? { href: detail(slug, id), label } : null;
  };
}

function byArg(
  argName: string,
  label: string,
  detail: (slug: string, id: string) => string,
): LinkBuilder {
  return (slug, action) => {
    const id = stringArg(action, argName);
    return id ? { href: detail(slug, id), label } : null;
  };
}

function page(label: string, href: (slug: string) => string): LinkBuilder {
  return (slug) => ({ href: href(slug), label });
}

const todo = byRecord("Open todo", routeUrl.todos.detail);
const goal = byRecord("Open goal", routeUrl.goals.detail);
const oneOnOne = byArg("oneOnOneId", "Open 1:1", routeUrl.oneOnOnes.detail);
const myLeave = page("Open your leave", routeUrl.leave.mine);
const teamLeave = page("Open team leave", routeUrl.leave.team);
const leaveTypesSetup = page("Open leave types", (slug) =>
  routeUrl.leave.setupTab(slug, "plans", "types"),
);
const leavePlansSetup = page("Open leave plans", (slug) =>
  routeUrl.leave.setupTab(slug, "plans", "plans"),
);
const attendanceCalendarsSetup = page("Open calendars", (slug) =>
  routeUrl.attendance.setupTab(slug, "calendars"),
);
const attendanceShiftsSetup = page("Open shifts", (slug) =>
  routeUrl.attendance.setupTab(slug, "shifts"),
);
const myAttendance = page("Open attendance", routeUrl.attendance.mine);

/// Deletes and anything not listed have no destination worth a link, so they are absent on purpose.
const LINKS: Record<string, LinkBuilder> = {
  "todos.create": todo,
  "todos.complete": todo,
  "todos.reopen": todo,
  "goals.create": goal,
  "goals.progress.update": goal,
  "goals.checkIns.create": byArg("goalId", "Open goal", routeUrl.goals.detail),
  "feedback.create": byRecord("Open feedback", routeUrl.feedback.detail),
  "kudos.create": byRecord("Open kudos", routeUrl.kudos.detail),
  "pulse.actions.create": byRecord("Open action", routeUrl.actions.detail),
  "leave.applications.create": myLeave,
  "leave.applications.cancel": myLeave,
  "leave.compOff.create": myLeave,
  "leave.compOff.cancel": myLeave,
  "leave.applications.decide": teamLeave,
  "leave.compOff.decide": teamLeave,
  "leave.types.create": leaveTypesSetup,
  "leave.types.update": leaveTypesSetup,
  "leave.policies.create": leavePlansSetup,
  "leave.policies.update": leavePlansSetup,
  "leave.policyAssignments.create": leavePlansSetup,
  "attendance.settings.update": page("Open attendance settings", routeUrl.attendance.setup),
  "attendance.calendars.create": attendanceCalendarsSetup,
  "attendance.calendars.update": attendanceCalendarsSetup,
  "attendance.defaultCalendar.update": attendanceCalendarsSetup,
  "attendance.calendarWeekOffs.update": attendanceCalendarsSetup,
  "attendance.calendarHolidays.create": attendanceCalendarsSetup,
  "attendance.holidayImports.create": attendanceCalendarsSetup,
  "attendance.shifts.create": attendanceShiftsSetup,
  "attendance.shifts.update": attendanceShiftsSetup,
  "attendance.shiftAssignments.create": attendanceShiftsSetup,
  "attendance.shiftAssignments.update": attendanceShiftsSetup,
  "attendance.checkins.create": myAttendance,
  "attendance.requests.create": myAttendance,
  "attendance.requests.cancel": myAttendance,
  "attendance.requests.decide": page("Open team attendance", routeUrl.attendance.team),
  "people.invitations.create": page("Open people", routeUrl.people.list),
  "oneOnOnes.actionItems.create": oneOnOne,
  "oneOnOnes.agendaItems.create": oneOnOne,
  "oneOnOnes.notes.update": oneOnOne,
};

export function copilotActionRecordLink(
  action: CopilotActionView,
  slug: string,
): RecordLink | null {
  if (action.status !== "confirmed") return null;
  return LINKS[action.capability]?.(slug, action) ?? null;
}
